/**
 * BufferSeriesCard — one card per buffer *family* (e.g. "buffer-tech15"), with a
 * month dropdown that swaps the displayed monthly fund (ticker + View Fund link)
 * without navigating. Drops into the same /products grid as <FundCard>.
 *
 * Visual port of the static catalog buffer card (products catelog.html) +
 * behaviour port of dane's buffer-series-card.tsx — but ALL data is read
 * straight off the `Fund` prop's `bufferConfig` (no `useFundDetail` fetch).
 */
import type { Fund } from "@/data/funds";
import { formatAsOfMdy } from "@/hooks/use-fund-live-data";
import { fundImageUrl } from "@/lib/api-client";
import { BUFFER_MONTH_ORDER, bufferMonthKey } from "@/lib/fund-buffer-series";
import { typeDefaultCardImage } from "@/lib/fund-card-image";
import { fundDetailPath } from "@/lib/fund-paths";
import { cn } from "@/lib/utils";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";

// JS Date.getMonth() (0=Jan…11=Dec) → 3-char month key, for default selection.
const JS_MONTH_TO_KEY = [
  "jan",
  "feb",
  "mar",
  "apr",
  "may",
  "jun",
  "jul",
  "aug",
  "sep",
  "oct",
  "nov",
  "dec",
] as const;

/** Calendar index of a fund's month within the May→Apr outcome year (last if unknown). */
function monthIndex(fund: Fund): number {
  const idx = BUFFER_MONTH_ORDER.indexOf(
    bufferMonthKey(fund.bufferConfig?.seriesMonth) as (typeof BUFFER_MONTH_ORDER)[number],
  );
  return idx === -1 ? BUFFER_MONTH_ORDER.length : idx;
}

/**
 * Default selection: the most-recent month whose outcome period has started
 * (≤ today). Walks the outcome-year calendar backward from today's month and
 * picks the first family fund that matches; falls back to the last fund.
 */
function resolveDefaultId(seriesFunds: [Fund, ...Fund[]]): string {
  const todayKey = JS_MONTH_TO_KEY[new Date().getMonth()] ?? "may";
  const todayIdx = BUFFER_MONTH_ORDER.indexOf(todayKey);
  for (let i = BUFFER_MONTH_ORDER.length - 1; i >= 0; i--) {
    if (todayIdx >= 0 && i > todayIdx) continue;
    const key = BUFFER_MONTH_ORDER[i];
    const fund = seriesFunds.find((f) => bufferMonthKey(f.bufferConfig?.seriesMonth) === key);
    if (fund) return fund.id;
  }
  const last = seriesFunds[seriesFunds.length - 1];
  return last ? last.id : seriesFunds[0].id;
}

/**
 * Buffer level "15.00" → "15%", "12.50" → "12.5%"; null/blank/non-numeric → null.
 * Uses parseFloat (not Number) so an admin-entered "15%" parses to 15 rather
 * than NaN — matches parseDisplayNumber in fund-detail-mapper.ts.
 */
function formatBufferLevel(raw: string | null | undefined): string | null {
  if (raw == null || raw.trim() === "") return null;
  const n = Number.parseFloat(raw);
  if (!Number.isFinite(n)) return null;
  return `${Number(n.toFixed(2))}%`;
}

/** Drop a trailing " — June Series" / " - June Series" tag from a full name. */
function stripSeriesSuffix(fullName: string | null | undefined): string {
  return (fullName ?? "").replace(/\s*[—-]\s*\w+\s+Series\s*$/i, "");
}

/** Month label for the trigger/options, e.g. "June" (falls back to "Series"). */
function monthLabel(fund: Fund): string {
  const raw = fund.bufferConfig?.seriesMonth?.trim();
  return raw && raw.length > 0 ? raw : "Series";
}

interface MonthOptionProps {
  fund: Fund;
  isSelected: boolean;
  onSelect: () => void;
}

/** One row in the dropdown panel: month name + outcome-period dates + ✓ when active. */
function MonthOption({ fund, isSelected, onSelect }: MonthOptionProps) {
  const start = formatAsOfMdy(fund.bufferConfig?.outcomePeriodStart);
  const end = formatAsOfMdy(fund.bufferConfig?.outcomePeriodEnd);

  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        "flex items-center justify-between w-full px-3 py-2 bg-transparent border-none text-left cursor-pointer transition-[background] duration-[120ms] font-[family-name:var(--font-geist)] text-[13px] hover:bg-white/[0.04]",
        isSelected ? "text-[var(--etf-orange)]" : "text-[var(--etf-white)]",
      )}
    >
      <span>
        {monthLabel(fund)}{" "}
        {start && end && (
          <span className="text-[11px] text-[var(--etf-white3)] font-[family-name:var(--font-instrument)]">
            {start} &ndash; {end}
          </span>
        )}
      </span>
      {isSelected && (
        <span className="text-[var(--etf-orange)] text-[14px] leading-none">&#10003;</span>
      )}
    </button>
  );
}

export function BufferSeriesCard({ seriesFunds }: { seriesFunds: [Fund, ...Fund[]] }) {
  const [selectedId, setSelectedId] = useState<string>(() => resolveDefaultId(seriesFunds));
  const [panelOpen, setPanelOpen] = useState(false);
  // Records the exact remote-image src that 404'd, so switching months (which may
  // carry a different, working image) still tries that fund's remote image first.
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);

  // Close the panel on outside mousedown.
  useEffect(() => {
    function handleDocClick(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setPanelOpen(false);
      }
    }
    document.addEventListener("mousedown", handleDocClick);
    return () => document.removeEventListener("mousedown", handleDocClick);
  }, []);

  // Dropdown order: calendar May → Apr (input order is already AUM-grouped).
  const sortedSeriesFunds = useMemo(
    () => [...seriesFunds].sort((a, b) => monthIndex(a) - monthIndex(b)),
    [seriesFunds],
  );

  const selectedFund: Fund = seriesFunds.find((f) => f.id === selectedId) ?? seriesFunds[0];
  const buffer = selectedFund.bufferConfig;

  const remoteImage = fundImageUrl(selectedFund.imageKeys?.[0]);
  const imageSrc =
    remoteImage && remoteImage !== failedSrc ? remoteImage : typeDefaultCardImage("buffer");

  const seriesBaseName = stripSeriesSuffix(selectedFund.fullName) || selectedFund.name;
  const triggerMonthLabel = monthLabel(selectedFund);
  const outcomeStart = formatAsOfMdy(buffer?.outcomePeriodStart);
  const outcomeEnd = formatAsOfMdy(buffer?.outcomePeriodEnd);
  const bufferLevel = formatBufferLevel(buffer?.bufferLevel);

  return (
    <div className="etf-fund-card group relative z-[1] hover:z-[50] overflow-visible flex flex-col no-underline text-inherit bg-[var(--etf-black)] transition-[background] duration-200 hover:bg-[var(--etf-black2)] cursor-default">
      {/* Image section */}
      <div
        className={cn(
          "etf-fund-card-image relative h-[200px] bg-[var(--etf-black)] flex flex-col justify-end px-[18px] py-4",
          "after:content-[''] after:absolute after:inset-0 after:bg-[linear-gradient(to_top,rgba(0,0,0,0.75)_0%,transparent_55%)] after:z-[1]",
        )}
      >
        <img
          src={imageSrc}
          alt={`${selectedFund.ticker} — ${selectedFund.name}`}
          loading="lazy"
          onError={
            remoteImage && remoteImage !== failedSrc ? () => setFailedSrc(remoteImage) : undefined
          }
          className="absolute inset-0 w-full h-full object-cover object-top opacity-35 z-0 transition-[opacity,filter] duration-200 group-hover:opacity-100 group-hover:brightness-110"
        />

        {/* Category pill — top-right (buffer = blue) */}
        <div className="absolute top-3.5 right-3.5 z-[3]">
          <span className="etf-fc-cat font-[family-name:var(--font-instrument)] text-[10px] tracking-[0.12em] uppercase px-2.5 py-1 border border-solid text-[#64b4ff] border-[rgba(100,180,255,0.4)] bg-[rgba(100,180,255,0.12)]">
            Buffer
          </span>
        </div>

        {/* Ticker + series name + outcome period */}
        <div className="relative z-[2]">
          <div className="font-[family-name:var(--font-f37bolton)] text-[36px] leading-none tracking-[-0.02em] text-white">
            {selectedFund.ticker}
          </div>
          <div className="font-[family-name:var(--font-geist)] text-[13px] font-medium text-white/65 mt-[5px]">
            {seriesBaseName}
          </div>
          {outcomeStart && outcomeEnd && (
            <div className="font-[family-name:var(--font-geist)] text-[11px] text-white/45 mt-[3px]">
              Outcome: {outcomeStart} &ndash; {outcomeEnd}
            </div>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="px-[18px] pt-[18px] pb-4 flex flex-col flex-1 border-t border-[var(--etf-border)] gap-0">
        {/* Stats row: Reference Asset / Buffer Level / Expense Ratio */}
        <div className="flex">
          <div className="flex-1 min-w-0">
            <div className="font-[family-name:var(--font-instrument)] text-[11px] font-medium text-[var(--etf-white)] leading-none mb-1 whitespace-nowrap overflow-hidden text-ellipsis">
              {buffer?.referenceTicker ?? "—"}
            </div>
            <div className="font-[family-name:var(--font-instrument)] text-[7.5px] tracking-[0.1em] uppercase text-[var(--etf-white3)] leading-[1.4]">
              Reference Asset
            </div>
          </div>
          <div className="flex-1 min-w-0 pl-3.5 border-l border-[var(--etf-border2)]">
            <div className="font-[family-name:var(--font-instrument)] text-[11px] font-medium text-[var(--etf-white)] leading-none mb-1 whitespace-nowrap overflow-hidden text-ellipsis">
              {bufferLevel ?? "—"}
            </div>
            <div className="font-[family-name:var(--font-instrument)] text-[7.5px] tracking-[0.1em] uppercase text-[var(--etf-white3)] leading-[1.4]">
              Buffer Level
            </div>
          </div>
          {/* Expense ratio — gross/net dual-line with fee-waiver tooltip */}
          <div className="group/expense flex-1 min-w-0 pl-3.5 border-l border-[var(--etf-border2)] relative cursor-help">
            <div className="flex items-baseline gap-[3px] leading-none mb-[2px]">
              <span className="font-[family-name:var(--font-instrument)] text-[10px] font-normal text-[var(--etf-white3)]">
                {selectedFund.expenseGross ?? "—"}
              </span>
              <span className="font-[family-name:var(--font-instrument)] text-[7.5px] tracking-[0.1em] uppercase text-[var(--etf-white3)]">
                Gross
              </span>
            </div>
            <div className="flex items-baseline gap-[3px] leading-none mb-[2px]">
              <span className="font-[family-name:var(--font-instrument)] text-[11px] font-medium text-[var(--etf-orange)]">
                {selectedFund.expenseRatio}
              </span>
              <span className="font-[family-name:var(--font-instrument)] text-[7.5px] tracking-[0.1em] uppercase text-[var(--etf-white3)]">
                Net
              </span>
            </div>
            <div className="font-[family-name:var(--font-instrument)] text-[7.5px] tracking-[0.1em] uppercase text-[var(--etf-white3)] leading-[1.4] mt-[2px]">
              Expense Ratio{" "}
              <span className="text-[9px] align-middle text-[var(--etf-white3)] ml-[2px] transition-colors duration-150 group-hover/expense:text-[var(--etf-orange)]">
                &#9432;
              </span>
            </div>
            <span
              className="absolute top-[calc(100%+10px)] left-1/2 -translate-x-1/2 w-[300px] bg-[#131313] text-[var(--etf-white2)] border border-[var(--etf-border)] rounded-md px-3.5 py-2.5 shadow-[0_8px_32px_rgba(0,0,0,0.9)] font-[family-name:var(--font-geist)] text-[11.5px] font-normal leading-[1.5] normal-case tracking-[0.02em] text-left whitespace-normal z-[100] pointer-events-none opacity-0 invisible transition-[opacity,visibility] duration-200 group-hover/expense:opacity-100 group-hover/expense:visible before:content-[''] before:absolute before:bottom-full before:left-1/2 before:border-solid before:border-transparent before:border-b-[var(--etf-border)] before:border-[7px] before:-ml-[7px] before:pointer-events-none after:content-[''] after:absolute after:bottom-full after:left-1/2 after:border-solid after:border-transparent after:border-b-[#131313] after:border-[6px] after:-ml-[6px] after:pointer-events-none"
              role="tooltip"
            >
              <span className="flex items-center justify-between mb-1">
                <strong className="text-[var(--etf-orange)] font-medium">Note</strong>
                <span className="opacity-50">{"×"}</span>
              </span>
              Corgi Strategies, LLC (the &ldquo;Adviser&rdquo;) has contractually agreed to waive a
              portion of its management fee equal to {selectedFund.expenseRatioDiscount ?? "0.10%"}{" "}
              of the Fund&rsquo;s average daily net assets. This agreement has no termination date
              and may not be terminated without the approval of the Board of Trustees, upon not less
              than thirty (30) days&rsquo; prior written notice to the Adviser; provided that the
              Board may not terminate the agreement with respect to any Fund prior to the one-year
              anniversary of the effective date of the agreement with respect to such Fund. Amounts
              waived are not subject to recoupment by the Adviser.
            </span>
          </div>
        </div>

        {/* Month dropdown picker */}
        <div ref={wrapRef} className="relative mt-4 mb-3 px-0">
          <button
            type="button"
            onClick={() => setPanelOpen((o) => !o)}
            aria-expanded={panelOpen}
            className={cn(
              "flex items-center justify-between w-full px-3 py-[9px] border rounded-md bg-[var(--etf-black2)] text-[var(--etf-white)] font-[family-name:var(--font-geist)] text-[13px] cursor-pointer transition-[border-color] duration-150",
              panelOpen
                ? "border-[var(--etf-orange)]"
                : "border-[var(--etf-border)] hover:border-[var(--etf-orange)]",
            )}
          >
            <span className="flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-[var(--etf-orange)] shrink-0" />
              {triggerMonthLabel} Series
            </span>
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className={cn(
                "opacity-50 shrink-0 transition-transform duration-200",
                panelOpen && "rotate-180",
              )}
              aria-hidden="true"
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>

          {panelOpen && (
            <div className="absolute left-0 right-0 top-full -mt-1.5 bg-[var(--etf-black2)] border border-[var(--etf-border)] rounded-md z-[200] max-h-60 overflow-y-auto shadow-[0_8px_24px_rgba(0,0,0,0.3)]">
              <div className="font-[family-name:var(--font-instrument)] text-[9.5px] tracking-[0.12em] uppercase text-[var(--etf-white3)] px-3 pt-[10px] pb-[6px] border-b border-[var(--etf-border2)]">
                Select Outcome Period
              </div>
              {sortedSeriesFunds.map((fund) => (
                <MonthOption
                  key={fund.id}
                  fund={fund}
                  isSelected={fund.id === selectedFund.id}
                  onSelect={() => {
                    setSelectedId(fund.id);
                    setPanelOpen(false);
                  }}
                />
              ))}
            </div>
          )}
        </div>

        {/* CTA row */}
        <div className="flex gap-0 mt-auto">
          <Link
            to={fundDetailPath(selectedFund.id)}
            className="flex-1 font-[family-name:var(--font-instrument)] text-[11.5px] tracking-[0.06em] py-[11px] px-2.5 border border-[var(--etf-border)] -mr-px bg-transparent text-[var(--etf-white2)] no-underline text-center transition-all duration-[180ms] ease-in-out hover:border-[var(--etf-orange)] hover:text-[var(--etf-orange)] hover:bg-[rgba(255,85,0,0.04)] hover:z-10 relative"
          >
            View Fund &rarr;
          </Link>
          <span
            className="flex-1 font-[family-name:var(--font-instrument)] text-[11.5px] tracking-[0.06em] py-[11px] px-2.5 border border-[var(--etf-border)] bg-transparent text-[var(--etf-white3)] no-underline text-center opacity-40 cursor-not-allowed pointer-events-none"
            aria-disabled="true"
          >
            All Series &rarr;
          </span>
        </div>
      </div>
    </div>
  );
}
